import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { ConfirmationDTO } from '../../model/confirmationDTO';
import { catchError, ignoreElements, map, mergeMap, Observable, of, shareReplay, tap } from 'rxjs';
import { AuthService } from '../../_services/auth.service';
import { SharedService } from '../../_services/shared.service';
import { OfferService } from '../../_services/offer/offer.service';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { NgxSpinnerModule } from 'ngx-spinner';
import { ShoppingCartService } from '../../_services/shopping-cart.service';
import { Offer } from '../../model/offer';
import { ShoppingCartTableComponent } from '../shopping-cart-table/shopping-cart-table.component';
import { ThemeService } from '../../_services/theme.service';
import { LxResponsiveClassDirective } from '../../directives/lx-responsive-class.directive';
import { OfferAliasAcceptedDTO } from '@app/model/offerAliasAcceptedDTO';


@Component({
    selector: 'app-offer-confirmation-code',
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        ShoppingCartTableComponent,
        NgxSpinnerModule,
        LxResponsiveClassDirective
    ],
    templateUrl: './offer-confirmation-code.component.html',
    styleUrl: './offer-confirmation-code.component.scss'
})
export class OfferConfirmationCodeComponent implements OnInit {

  public propertyId: string = '';
  public offerAliasId: string = '';
  private token: string = '';
  public offerConfirmation$!: Observable<ConfirmationDTO[]>;
  public offerErrors$: any;
  public invalidCode: boolean = false;
  public offerError: boolean = false;
  public themeReady: boolean = false;

  public offer_alias_data: OfferAliasAcceptedDTO = {
    offerError: false,
    accepted: false,
    acknowledgementEmailSent: false,
    requestedOfferSetIds: [],
    requestedOfferIds: []
  };

  private readonly isBrowser: boolean;
  private readonly routeParamPattern = /^[A-Za-z0-9_-]{1,128}$/;

  constructor(private authService: AuthService,
              public sharedService: SharedService,
              public shoppingCartService: ShoppingCartService,
              private offerService: OfferService,
              private router: Router,
              private route: ActivatedRoute,
              private themeService: ThemeService,
              @Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }


  ngOnInit(): void {
    if (!this.isBrowser) {
      this.themeReady = false;
      return;
    }
    const propertyId = this.getValidatedRouteParam('prop_id');
    const offerAliasId = this.getValidatedRouteParam('alias_id');
    const token = this.getValidatedRouteParam('token');

    if (!propertyId || !offerAliasId || !token) {
      this.router.navigate(['/offerNotAvailable']);
      return;
    }

    this.propertyId = propertyId;
    this.offerAliasId = offerAliasId;
    this.token = token;

    this.waitForThemeReady();

    // Restore persisted browser theme/mode through the theme service.
    const prestayTheme = this.sharedService.applicationConfig?.prestay?.theme;

    this.themeService.restorePersistedThemeOrApply(prestayTheme, 'zen').finally(() => {
      this.themeReady = this.themeService.isThemeReady();
    });

    this.offerConfirmation$ = this.offerService.checkOfferAliasAccepted(this.propertyId, this.offerAliasId)
      .pipe(
        shareReplay(1),
        map((data: OfferAliasAcceptedDTO) => {
          this.offer_alias_data = data;
          this.offerError = this.offer_alias_data.offerError;
          return data;
        }),
        mergeMap((data: OfferAliasAcceptedDTO) => {
          if (data.accepted === true) {
            return this.getConfrimationInfo(this.offerAliasId, this.propertyId).pipe(
              catchError(err => {
                this.offerError = true;
                this.router.navigate(['/offerNotAvailable']);
                return of(err);
              }),
              shareReplay(1)
            );
          } else {
            if (data.offerError) {
              this.offerError = true;
              this.router.navigate(['/offerNotAvailable']);
              return of(null);
            }
            const confirmOfferAccepted$ = this.offerService.confirmOfferAccepted(
              this.propertyId,
              this.offerAliasId,
              data.requestedOfferSetIds,
              data.requestedOfferIds,
              this.token
            );

            return confirmOfferAccepted$.pipe(
              catchError(err => {
                if (err?.status === 400) {
                  this.invalidCode = true;
                }
                this.offerError = true;
                this.router.navigate(['/offerNotAvailable']);
                return of(err);
              }),
              shareReplay(1)
            );
          }
        }),
        shareReplay(1)
      );

    this.offerErrors$ = this.offerConfirmation$.pipe(
      ignoreElements(),
      catchError(err => {
        this.router.navigate(['/offerNotAvailable']);
        return of(err);
      }),
      shareReplay(1)
    );


  }

  getConfrimationInfo(offerAliasId: string, propertyId: string): Observable<ConfirmationDTO[]> {
    return this.offerService.getConfrimationInfo(offerAliasId, propertyId).pipe(shareReplay(1));
  }

  private getOfferIdentifiers(confirmation: ConfirmationDTO[]): { offerSetIds: string[]; offerIds: string[] } {
    const offerSetIds = [...new Set((confirmation || []).map(item => item.offer_set_id).filter(Boolean))];
    const offerIds = [...new Set((confirmation || []).map(item => item.offer_id).filter(Boolean))];

    return { offerSetIds, offerIds };
  }

  private getValidatedRouteParam(name: string): string | null {
    const value = this.route.snapshot.paramMap.get(name);
    if (!value || !this.routeParamPattern.test(value)) {
      return null;
    }
    return value;
  }

  navigateToOfferStatus(){
    this.router.navigate(['/offerStatus', this.offerAliasId, this.propertyId]);
  }

  private waitForThemeReady(): void {
    this.themeReady = this.themeService.isThemeReady();

    if (this.themeReady) {
      return;
    }

    this.themeService.themeReady$.subscribe(isReady => {
      this.themeReady = isReady;
    });
  }

  getTotalQuantity(offerConfirmation: any[]): number {
    return offerConfirmation.reduce((acc, conf) => acc + (conf.effective_freq === 'DAILY' ? conf.los : 1), 0);
  }

  getTotalPrice(offerConfirmation: any[]): number {
    return offerConfirmation.reduce((acc, conf) => acc + conf.ofr_total_price, 0);
  }


  public calculateTotalQuantity(offer: any[]): number {
    return this.shoppingCartService.calculateTotalQuantity(offer);
  }

  public calculateTotalPrice(offer: any[]): number {
    return this.shoppingCartService.calculateTotalPrice(offer);
  }

  public calculateQuantity(offer: any): number {
    return this.shoppingCartService.calculateQuantity(offer);
  }

  public calculatePrice(offer: any): number {
    return this.shoppingCartService.calculatePrice(offer);
  }

}
